import { Injectable } from '@angular/core';
import { Observable, forkJoin, of, concat } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { ReleaseReportService } from './releasereport.service';
import { UserProfileService } from './user-profile.service';
import { EnvironmentService } from './environment.service';
import { CacheService } from './cache.service';
import {
  Release,
  ReleaseReportReleasesResponse,
  ReviewClassification,
} from '../models/release';
import { OwnershipEntry } from '../models/ownership-entry';

/**
 * Loads and processes the release report into Release[] (with team + links). Used by the routed
 * Release Retro view so it can load on direct URL navigation, independent of the grid component.
 * Team/link resolution uses the environment's default org/project (the same values the grid
 * resolves to), so a full org/project lookup isn't needed.
 */
@Injectable({ providedIn: 'root' })
export class ReleasesDataService {
  /**
   * Default fetch window (days). Kept small so the first/cold load stays fast; the retro's cutoff
   * picker widens it on demand. The grid covers the same window with its own range selector.
   */
  static readonly DEFAULT_WINDOW_DAYS = 90;
  private static readonly CACHE_PREFIX = 'rel-retro';
  /** The grid component's cache prefix — kept here so a write-through edit can patch both caches. */
  static readonly GRID_CACHE_PREFIX = 'rel-grid';
  private static readonly CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12h

  private readonly org: string;
  private readonly project: string;

  constructor(
    private readonly releaseReportService: ReleaseReportService,
    private readonly userProfileService: UserProfileService,
    private readonly cacheService: CacheService,
    environmentService: EnvironmentService
  ) {
    const env = environmentService.getEnvironment();
    this.org = env.defaultOrg;
    this.project = env.defaultProject;
  }

  /**
   * Releases within the last `windowDays`, processed with team + links.
   *
   * Stale-while-revalidate: when a fresh-enough copy is cached (localStorage) it is emitted
   * immediately for an instant first paint, then a second emission follows with freshly fetched
   * data so edits / new releases are picked up without a hard reload. Pass `forceReload` to skip
   * the cached paint and fetch only (e.g. when widening the window).
   */
  getReleases(
    windowDays = ReleasesDataService.DEFAULT_WINDOW_DAYS,
    forceReload = false
  ): Observable<Release[]> {
    // 'v2' versions the cached schema: a prior build cached a different shape under this key, so
    // bump the version whenever the cached payload shape changes to avoid reading a stale shape.
    const key = this.cacheService.generateCacheKey(ReleasesDataService.CACHE_PREFIX, [
      'v2',
      this.org,
      String(windowDays),
    ]);
    const now = new Date();
    const startDate = new Date(now);
    startDate.setDate(now.getDate() - windowDays);

    const fresh$ = forkJoin({
      releases: this.releaseReportService.getReleases(
        startDate.toISOString(),
        now.toISOString()
      ),
      ownership: this.userProfileService
        .getRepoOwnershipMaster(this.org)
        .pipe(catchError(() => of([] as OwnershipEntry[]))),
    }).pipe(
      // Process first so the emitted value never depends on the (best-effort) cache write.
      map((raw) => this.process(raw.releases, raw.ownership)),
      tap((processed) => this.cacheService.set(key, ReleasesDataService.toCacheProjection(processed)))
    );

    if (!forceReload) {
      const cached = this.cacheService.get<Release[]>(key, ReleasesDataService.CACHE_TTL_MS);
      if (Array.isArray(cached)) {
        return concat(of(ReleasesDataService.fromCacheProjection(cached)), fresh$);
      }
    }
    return fresh$;
  }

  /** Drop cached releases so the next load fetches fresh. */
  invalidate(): void {
    this.cacheService.clearByPrefix(ReleasesDataService.CACHE_PREFIX);
  }

  /**
   * Write an edited release through to every cached releases list — both the retro cache and the
   * grid cache — so a reload reflects the change immediately instead of showing the pre-edit copy.
   * The per-load background revalidate still reconciles against the server, so an edit that didn't
   * actually persist will self-correct on the next fetch rather than being masked forever.
   */
  applyEdit(updated: Release): void {
    const [trimmed] = ReleasesDataService.toCacheProjection([updated]);
    const patch = (items: Release[]): Release[] =>
      items.map((r) => (r.id === updated.id ? { ...r, ...trimmed } : r));
    this.cacheService.patchByPrefix<Release>(ReleasesDataService.CACHE_PREFIX, patch);
    this.cacheService.patchByPrefix<Release>(ReleasesDataService.GRID_CACHE_PREFIX, patch);
  }

  /**
   * Lightweight cache projection: keeps the fields the retro/grid render (team, dates, statuses,
   * links) but drops the two heavy fields (releaseEnvironments, description) so the payload fits
   * the ~5MB localStorage quota. They're only used in the edit dialog and are refilled on revalidate.
   *
   * Single source of truth for BOTH caches: the grid (`ReleasesComponent`) and `applyEdit`'s
   * cross-cache write-through both call these, so the `rel-grid` and `rel-retro` payloads can't
   * drift into divergent shapes.
   */
  static toCacheProjection(releases: Release[]): Release[] {
    return releases.map((r) => ({ ...r, description: [], releaseEnvironments: [] }));
  }

  /** Re-hydrates a cached projection: dates come back as strings from JSON, so parse them. */
  static fromCacheProjection(cached: Release[]): Release[] {
    return (Array.isArray(cached) ? cached : []).map((r) => ({
      ...r,
      releaseStartedDate: new Date(r.releaseStartedDate),
      releaseStartedDateLocal: new Date(r.releaseStartedDateLocal),
      description: [],
      releaseEnvironments: [],
    }));
  }

  private process(
    releases: ReleaseReportReleasesResponse[],
    ownership: OwnershipEntry[]
  ): Release[] {
    const result: Release[] = [];
    (releases ?? []).forEach((response) => {
      const release = this.toRelease(response, ownership);
      if (release) {
        result.push(release);
      }
    });
    return result;
  }

  private toRelease(
    response: ReleaseReportReleasesResponse,
    ownership: OwnershipEntry[]
  ): Release | null {
    try {
      const rel = response.Release as any;
      const startedUtc = rel.StartedDateTimeUtc;
      return {
        id: rel.ReleaseId,
        adoReleaseId: rel.AdoReleaseId,
        branchName: rel.BranchName,
        pipelineName: rel.PipelineName,
        releaseName: rel.ReleaseName,
        isYaml: rel.IsYaml,
        releaseStartedDate: new Date(startedUtc),
        releaseStartedDateLocal: new Date(
          new Date(startedUtc).toLocaleString('en-US', {
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          })
        ),
        reviewed: rel.Reviewed ?? false,
        releaseStatus: rel.Success === null ? true : rel.Success,
        team: this.getTeam(rel.PipelineName, rel.IsYaml, ownership),
        releaseLink: rel.IsYaml
          ? this.userProfileService.getYamlReleaseLink(this.org, this.project, rel.AdoReleaseId)
          : this.userProfileService.getClassicReleaseLink(this.org, this.project, rel.AdoReleaseId),
        description: (response.Details ?? []).map((x: any) => ({
          Description: x.Description,
          DescriptionId: x.DescriptionId,
        })),
        changeFail:
          rel.ChangeFail === null || rel.ChangeFail === undefined
            ? rel.Success === null
              ? true
              : rel.Success
            : rel.ChangeFail,
        isTransient: rel.IsTransient ?? false,
        reviewClassification:
          rel.ReviewClassification === null || rel.ReviewClassification === undefined
            ? rel.Reviewed
              ? ReviewClassification.Reviewed
              : ReviewClassification.NeedsReview
            : (rel.ReviewClassification as ReviewClassification),
        releaseEnvironments: rel.ReleaseEnvironments,
      };
    } catch (error) {
      console.error('Error processing release:', error);
      return null;
    }
  }

  private getTeam(
    pipelineName: string,
    isYaml: boolean,
    ownership: OwnershipEntry[]
  ): string {
    const propertyGroup = isYaml ? 'yamlPipelines' : 'releaseDefinitions';
    const result = (ownership || [])
      .filter((entry: any) =>
        entry[propertyGroup]?.some(
          (pipeline: { name: string }) => pipeline.name === pipelineName
        )
      )
      .map((entry: any) => entry.team);
    return result.length > 0 ? result[0] : '';
  }
}
